import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Card } from "../components/ui/card"
import { Spinner } from "../components/ui/spinner"
import { StatusPill } from "../components/ui/status-pill"
import { cn } from "../lib/cn"
import { useRunsList } from "../lib/hooks"

type Filter = "all" | "success" | "partial" | "failed" | "running"

const FILTERS: Filter[] = ["all", "success", "partial", "failed", "running"]

// "running" 選択時は queued + running の OR フィルタを送る。
// (backend status_filter はカンマ区切りで OR 指定可 — Phase 4.2 レビュー M-8)
function filterToStatusQuery(f: Filter): string | undefined {
  if (f === "all") return undefined
  if (f === "running") return "running,queued"
  return f
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return "今"
  if (diff < 3600) return `${Math.floor(diff / 60)} 分前`
  if (diff < 86400) return `${Math.floor(diff / 3600)} 時間前`
  return d.toLocaleDateString("ja-JP")
}

export function RunsList() {
  const [filter, setFilter] = useState<Filter>("all")
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data, isLoading, error } = useRunsList({
    status_filter: filterToStatusQuery(filter),
    search: debouncedSearch || undefined,
    limit: 50,
  })

  return (
    <div className="flex flex-col gap-7 pb-20 pt-10 px-20">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] font-semibold leading-9 text-text-primary">Runs</h1>
          <p className="text-sm text-text-secondary">過去の抽出結果。run_id をクリックで詳細へ。</p>
        </div>
        <Link
          to="/submit"
          className="inline-flex items-center rounded-lg bg-accent px-4 py-2.5 text-xs font-semibold text-text-on-accent hover:bg-accent/90"
        >
          + New run
        </Link>
      </div>

      <Card className="flex items-center gap-3 px-3.5 py-2.5">
        <span className="text-sm text-text-muted">⌕</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="タイトル / URL で検索"
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
        />
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                filter === f
                  ? "bg-accent text-text-on-accent"
                  : "bg-surface-2 text-text-secondary hover:text-text-primary",
              )}
            >
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : error ? (
        <Card className="p-6 text-sm text-danger">読み込みに失敗しました</Card>
      ) : !data || data.items.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-2 p-12 text-center">
          <p className="text-sm text-text-secondary">
            {debouncedSearch || filter !== "all"
              ? "条件に合致する run がありません"
              : "まだ run がありません"}
          </p>
          <Link to="/submit" className="text-sm text-accent hover:underline">
            最初の Run を実行する →
          </Link>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {data.items.map((r) => (
            <Link
              key={r.run_id}
              to={`/runs/${r.run_id}`}
              className="block rounded-xl border border-border-subtle bg-surface px-5 py-4 transition-colors hover:border-border-default"
            >
              <div className="flex items-center gap-5">
                <div className="h-14 w-24 shrink-0 rounded-md bg-surface-2" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <div className="flex items-center gap-2.5">
                    <StatusPill state={r.status} />
                    <span className="text-[15px] font-semibold text-text-primary">
                      {r.title ?? "(no title)"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3.5 text-xs">
                    <span className="text-text-muted">{r.run_id}</span>
                    {r.url ? (
                      <>
                        <span className="text-text-muted">·</span>
                        <span className="truncate text-text-muted">{r.url}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-7">
                  <Stat label="候補" value={String(r.candidates_count ?? 0)} />
                  <Stat label="開始" value={formatRelativeTime(r.started_at)} muted />
                </div>
                <span className="text-2xl font-medium text-text-muted">›</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className="text-[11px] text-text-muted">{label}</span>
      <span
        className={cn("text-sm font-semibold", muted ? "text-text-secondary" : "text-text-primary")}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * Set `value` after `delay` ms of `source` stability. We deliberately avoid
 * a generic `useDebouncedEffect(cb, delay, deps)` helper because biome's
 * exhaustive-deps lint cannot statically verify a passed-in `deps` array,
 * and a value-specific debouncer is what we actually need here.
 */
function useDebouncedValue<T>(source: T, delay: number): T {
  const [value, setValue] = useState<T>(source)
  useEffect(() => {
    const t = setTimeout(() => setValue(source), delay)
    return () => clearTimeout(t)
  }, [source, delay])
  return value
}
